import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Switch,
  IconButton,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue,
  Container,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Spinner,
  Center,
  Divider,
  List,
  ListItem,
  ListIcon,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { 
  AddIcon, 
  EditIcon, 
  DeleteIcon, 
  DragHandleIcon,
  ChevronRightIcon,
  ViewIcon,
  DownloadIcon,
  TimeIcon,
  CheckCircleIcon,
  WarningIcon,
  StarIcon,
  RepeatIcon,
  CopyIcon,
  InfoIcon,
} from '@chakra-ui/icons';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import componentTransformer from '../../../utils/componentTransformer';

const fetchSelectOptions = async (sourceType, tenantId) => {
  try {
    switch (sourceType) {
      case 'navigations':
        const navResponse = await apiClient(`/navigations/${tenantId}`);
        return (navResponse.data || navResponse || []).map(nav => ({
          value: nav.id,
          label: nav.name
        }));

      case 'content_models':
        const modelsResponse = await apiClient(`/content-models/${tenantId}`);
        return (modelsResponse.data || modelsResponse || []).map(model => ({
          value: model.id,
          label: model.name
        }));

      case 'content_entries':
        const entriesResponse = await apiClient(`/content-entries/${tenantId}`);
        return (entriesResponse.data || entriesResponse || []).map(entry => ({
          value: entry.id,
          label: entry.title || `Entry #${entry.id}`
        }));

      case 'taxonomies':
        const taxonomiesResponse = await apiClient(`/taxonomies/${tenantId}`);
        return (taxonomiesResponse.data || taxonomiesResponse || []).map(taxonomy => ({
          value: taxonomy.id,
          label: taxonomy.name
        }));

      case 'sites':
        const sitesResponse = await apiClient(`/sites/${tenantId}`);
        return (sitesResponse.data || sitesResponse || []).map(site => ({
          value: site.id,
          label: site.name
        }));

      default:
        return [];
    }
  } catch (error) {
    console.error(`Error fetching ${sourceType}:`, error);
    return [];
  }
};

const PageBuilder = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [page, setPage] = useState(null);
  const [layout, setLayout] = useState([]);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [isPropsModalOpen, setIsPropsModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [draggedComponent, setDraggedComponent] = useState(null);
  
  // Version management state
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [workingVersion, setWorkingVersion] = useState(null); // For unsaved changes
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [isRestoringVersion, setIsRestoringVersion] = useState(false);
  const { isOpen: isRestoreDialogOpen, onOpen: onRestoreDialogOpen, onClose: onRestoreDialogClose } = useDisclosure();
  const cancelRef = React.useRef();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sidebarBg = useColorModeValue('gray.50', 'gray.800');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Save working version to localStorage whenever layout changes
  useEffect(() => {
    if (pageId && layout.length > 0) {
      const workingVersionData = {
        layout,
        timestamp: Date.now(),
        isDirty
      };
      localStorage.setItem(`page-builder-${pageId}`, JSON.stringify(workingVersionData));
      setWorkingVersion(workingVersionData);
    }
  }, [layout, pageId, isDirty]);

  // Load working version from localStorage on mount
  useEffect(() => {
    if (pageId) {
      const savedWorkingVersion = localStorage.getItem(`page-builder-${pageId}`);
      if (savedWorkingVersion) {
        try {
          const workingData = JSON.parse(savedWorkingVersion);
          setWorkingVersion(workingData);
        } catch (error) {
          console.error('Error parsing working version:', error);
        }
      }
    }
  }, [pageId]);

  // Handle returning from preview - restore working version if it exists
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && workingVersion) {
        // User returned to the tab, restore working version if it's more recent
        const currentTime = Date.now();
        const timeDiff = currentTime - workingVersion.timestamp;
        
        // If working version is less than 1 hour old, restore it
        if (timeDiff < 3600000 && workingVersion.layout) {
          setLayout(workingVersion.layout);
          setIsDirty(workingVersion.isDirty);
          
          toast({
            title: 'Working version restored',
            description: 'Your unsaved changes have been restored',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [workingVersion, toast]);

  useEffect(() => {
    if (pageId && tenantId) {
      fetchPage();
      fetchAvailableComponents();
    }
  }, [pageId, tenantId]);

  const fetchPage = async () => {
  try {
    const response = await apiClient(`/pages/${tenantId}/${pageId}`);
    
    if (!response || response.error) {
      throw new Error(response?.error || 'Page not found');
    }
    
    setPage(response);
    
    // Load versions
    if (response.data?.versions && Array.isArray(response.data?.versions)) {
      const sortedVersions = response.data?.versions.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setVersions(sortedVersions);
      
      // Set current version (latest published or draft)
      const publishedVersion = sortedVersions.find(v => v.version_type === 'published');
      const draftVersion = sortedVersions.find(v => v.version_type === 'draft');
      const latestVersion = publishedVersion || draftVersion || sortedVersions[0];
      
      setCurrentVersion(latestVersion);
      
      // Load layout from current version or working version
      if (workingVersion && workingVersion.layout && workingVersion.layout.length > 0) {
        console.log('Loading working version:', workingVersion.layout);
        setLayout(workingVersion.layout);
        setIsDirty(workingVersion.isDirty);
      } else if (latestVersion && latestVersion.layout_data) {
        try {
          let parsedLayout = [];
          
          console.log('Loading from latest version, raw data:', latestVersion.layout_data);
          
          if (typeof latestVersion.layout_data === 'string') {
            // Try to parse the string
            const firstParse = JSON.parse(latestVersion.layout_data);
            
            // If the result is still a string, parse it again
            if (typeof firstParse === 'string') {
              parsedLayout = JSON.parse(firstParse);
            } else if (Array.isArray(firstParse)) {
              parsedLayout = firstParse;
            } else {
              parsedLayout = firstParse.layout || firstParse.components || [];
            }
          } else if (Array.isArray(latestVersion.layout_data)) {
            parsedLayout = latestVersion.layout_data;
          } else if (latestVersion.layout_data && typeof latestVersion.layout_data === 'object') {
            // Handle case where layout_data might be nested
            parsedLayout = latestVersion.layout_data.layout || latestVersion.layout_data.components || [];
          }
          
          console.log('Parsed layout in fetchPage:', parsedLayout);
          setLayout(Array.isArray(parsedLayout) ? parsedLayout : []);
        } catch (parseError) {
          console.error('Error parsing layout data:', parseError);
          setLayout([]);
        }
      } else {
        // No version data, start with empty layout
        setLayout([]);
      }
    } else {
      // No versions, start with empty layout
      setLayout([]);
      setVersions([]);
    }
  } catch (error) {
    console.error('Error fetching page:', error);
    toast({
      title: 'Error loading page',
      description: error.message || 'Failed to load page',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    navigate('/pages');
  } finally {
    setLoading(false);
  }
};

  const fetchAvailableComponents = async () => {
    try {
      const response = await apiClient(`/page-components/${tenantId}`);
      console.log('Available components response:', response);
      
      const components = response.data || response || [];
      
      const processedComponents = components.map(component => ({
        ...component,
        component_type: component.identifier || component.component_type || 'unknown',
        fields: typeof component.fields === 'string' ? JSON.parse(component.fields) : (component.fields || []),
        default_props: typeof component.default_data === 'string' ? JSON.parse(component.default_data) : (component.default_data || {})
      }));
      
      setAvailableComponents(processedComponents);
    } catch (error) {
      console.error('Error fetching components:', error);
      setAvailableComponents([]);
    }
  };

  const handleDragStart = useCallback((event) => {
    const { active } = event;
    setActiveId(active.id);
    if (active.id.toString().startsWith('component-')) {
      const componentId = active.id.toString().replace('component-', '');
      const component = availableComponents.find(c => c.id.toString() === componentId);
      setDraggedComponent(component);
    } else {
      const layoutItem = layout.find(item => item.id === active.id);
      setDraggedComponent(layoutItem);
    }
  }, [availableComponents, layout]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    setDraggedComponent(null);

    if (!over) return;

    if (active.id.toString().startsWith('component-') && over.id === 'layout-droppable') {
      const componentId = active.id.toString().replace('component-', '');
      const component = availableComponents.find(c => c.id.toString() === componentId);
      
      if (component) {
        let defaultProps = {};
        if (component.default_props) {
          try {
            defaultProps = typeof component.default_props === 'string' 
              ? JSON.parse(component.default_props) 
              : component.default_props;
          } catch (e) {
            console.warn('Could not parse default props:', e);
            defaultProps = {};
          }
        }

        if (Object.keys(defaultProps).length === 0) {
          switch (component.component_type) {
            case 'heading':
            case 'title':
              defaultProps = { text: 'New Heading', size: 'lg', color: '#000000' };
              break;
            case 'text':
            case 'paragraph':
              defaultProps = { content: 'Enter your text here...', size: 'md' };
              break;
            case 'button':
              defaultProps = { text: 'Click me', colorScheme: 'blue', size: 'md' };
              break;
            case 'image':
              defaultProps = { src: '', alt: 'Image', height: '200px' };
              break;
            case 'hero':
              defaultProps = { 
                title: 'Hero Title', 
                subtitle: 'Hero subtitle goes here',
                backgroundColor: '#f7fafc'
              };
              break;
            case 'card':
              defaultProps = { 
                title: 'Card Title', 
                content: 'Card content goes here...'
              };
              break;
            default:
              defaultProps = {};
          }
        }

        const newLayoutItem = {
          id: `${component.id}_${Date.now()}`,
          componentId: component.id,
          type: component.component_type,
          name: component.name,
          identifier: component.identifier,
          fields: component.fields || [],
          props: { ...defaultProps },
          children: []
        };

        setLayout(prevLayout => [...prevLayout, newLayoutItem]);
        setIsDirty(true);
      }
      return;
    }

    if (active.id !== over.id && layout.find(item => item.id === active.id)) {
      setLayout((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        
        return arrayMove(items, oldIndex, newIndex);
      });
      setIsDirty(true);
    }
  };

  const updateComponentProps = (componentId, newProps) => {
    setLayout(prevLayout => 
      prevLayout.map(item => 
        item.id === componentId 
          ? { ...item, props: { ...item.props, ...newProps } }
          : item
      )
    );
    setIsDirty(true);
  };

  const removeComponent = (componentId) => {
    setLayout(prevLayout => prevLayout.filter(item => item.id !== componentId));
    setIsDirty(true);
  };

  const saveLayout = async () => {
    try {
      // Transform layout to HTML content
      const htmlContent = componentTransformer.transformLayoutToHTML(layout);
      const completeHTML = componentTransformer.generateCompleteHTML(layout, {
        title: page.name,
        meta_title: page.meta_title,
        meta_description: page.meta_description
      });

      console.log('Generated HTML content:', htmlContent);
      console.log('Complete HTML page:', completeHTML);

      const response = await apiClient(`/pages/${tenantId}/${pageId}/versions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          version_type: 'draft',
          layout_data: JSON.stringify(layout), // Store JSON for editing
          content: htmlContent, // Store HTML for display
          full_html: completeHTML, // Store complete HTML page
          component_count: layout.length,
          generated_at: new Date().toISOString()
        })
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      setIsDirty(false);
      
      // Clear working version from localStorage
      localStorage.removeItem(`page-builder-${pageId}`);
      setWorkingVersion(null);
      
      // Refresh page data to get updated versions
      await fetchPage();
      
      toast({
        title: 'Layout saved',
        description: `Page layout saved with ${layout.length} components converted to HTML`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving layout:', error);
      toast({
        title: 'Error saving layout',
        description: error.message || 'Failed to save layout',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const publishPage = async () => {
    try {
      // First save the current layout as a draft
      await saveLayout();
      
      // Get the latest version ID (the one we just saved)
      const pageData = await apiClient(`/pages/${tenantId}/${pageId}`);
      const latestVersion = pageData.versions?.[0] || currentVersion;
      
      const response = await apiClient(`/pages/${tenantId}/${pageId}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          version_id: latestVersion?.id,
          publish_immediately: true,
          publish_date: new Date().toISOString()
        })
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      toast({
        title: 'Page published',
        description: 'Your page is now live',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh page data to update status and versions
      await fetchPage();
    } catch (error) {
      console.error('Error publishing page:', error);
      toast({
        title: 'Error publishing page',
        description: error.message || 'Failed to publish page',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const previewPage = () => {
    let previewLayout = layout;
    
    // If no current layout, try to get from current version
    if (layout.length === 0 && currentVersion?.layout_data) {
      try {
        const layoutData = typeof currentVersion.layout_data === 'string' ? 
          JSON.parse(currentVersion.layout_data) : currentVersion.layout_data;
        previewLayout = Array.isArray(layoutData) ? layoutData : [];
      } catch (e) {
        console.error('Error parsing layout for preview:', e);
        previewLayout = [];
      }
    }

    // Generate HTML for preview
    const htmlContent = componentTransformer.transformLayoutToHTML(previewLayout);
    const completeHTML = componentTransformer.generateCompleteHTML(previewLayout, {
      title: page.name || 'Preview',
      meta_title: page.meta_title || page.name,
      meta_description: page.meta_description
    });

    const previewData = {
      page,
      layout: previewLayout,
      htmlContent,
      completeHTML,
      version: currentVersion,
      hasUnsavedChanges: isDirty
    };
    
    sessionStorage.setItem(`page-preview-${pageId}`, JSON.stringify(previewData));
    window.open(`/dashboard/pages/${pageId}/preview`, '_blank');
  };

  const exportHTML = () => {
    const completeHTML = componentTransformer.generateCompleteHTML(layout, {
      title: page.name || 'Exported Page',
      meta_title: page.meta_title || page.name,
      meta_description: page.meta_description
    });

    // Create download link
    const blob = new Blob([completeHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${page.name || 'page'}-${Date.now()}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'HTML exported',
      description: 'Page HTML has been downloaded',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const loadVersion = (version) => {
  try {
    const layoutData = version.layout_data;
    let parsedLayout = [];
    
    console.log('Raw layout data:', layoutData);
    console.log('Layout data type:', typeof layoutData);
    
    // Handle different data formats
    if (typeof layoutData === 'string') {
      try {
        // First, try to parse the string as JSON
        const firstParse = JSON.parse(layoutData);
        console.log('First parse result:', firstParse);
        
        // If the result is still a string, parse it again
        if (typeof firstParse === 'string') {
          parsedLayout = JSON.parse(firstParse);
        } else if (Array.isArray(firstParse)) {
          parsedLayout = firstParse;
        } else {
          // If it's an object, try to find the layout array
          parsedLayout = firstParse.layout || firstParse.components || [];
        }
      } catch (parseError) {
        console.error('Error parsing layout data string:', parseError);
        parsedLayout = [];
      }
    } else if (Array.isArray(layoutData)) {
      parsedLayout = layoutData;
    } else if (layoutData && typeof layoutData === 'object') {
      // Handle case where layout_data might be an object with layout property
      parsedLayout = layoutData.layout || layoutData.components || [];
    }
    
    // Ensure we have a valid array
    if (!Array.isArray(parsedLayout)) {
      console.warn('Parsed layout is not an array:', parsedLayout);
      parsedLayout = [];
    }
    
    console.log('Final parsed layout:', parsedLayout);
    console.log('Layout length:', parsedLayout.length);
    
    setLayout(parsedLayout);
    setCurrentVersion(version);
    setIsDirty(false);
    
    // Clear working version since we're loading a specific version
    localStorage.removeItem(`page-builder-${pageId}`);
    setWorkingVersion(null);
    
    toast({
      title: 'Version loaded',
      description: `Loaded ${version.version_type} version with ${parsedLayout.length} components from ${new Date(version.created_at).toLocaleString()}`,
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  } catch (error) {
    console.error('Error loading version:', error);
    toast({
      title: 'Error loading version',
      description: 'Failed to load version data',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }
};

  const handleRestoreVersion = (versionId) => {
    setSelectedVersionId(versionId);
    onRestoreDialogOpen();
  };

  const confirmRestoreVersion = async () => {
  if (!selectedVersionId) return;
  
  setIsRestoringVersion(true);
  
  try {
    const versionToRestore = versions.find(v => v.id === selectedVersionId);
    if (!versionToRestore) {
      throw new Error('Version not found');
    }
    
    // Parse the layout data properly
    let layoutData = versionToRestore.layout_data;
    
    console.log('Restoring version, raw data:', layoutData);
    
    if (typeof layoutData === 'string') {
      try {
        // First parse
        const firstParse = JSON.parse(layoutData);
        
        // If still a string, parse again
        if (typeof firstParse === 'string') {
          layoutData = JSON.parse(firstParse);
        } else {
          layoutData = firstParse;
        }
      } catch (e) {
        console.error('Error parsing layout data for restore:', e);
        layoutData = [];
      }
    }
    
    console.log('Parsed layout data for restore:', layoutData);
    
    // Transform the restored layout to HTML
    const htmlContent = componentTransformer.transformLayoutToHTML(layoutData);
    const completeHTML = componentTransformer.generateCompleteHTML(layoutData, {
      title: page.name,
      meta_title: page.meta_title,
      meta_description: page.meta_description
    });
    
    // Create a new draft version based on the selected version
    const response = await apiClient(`/pages/${tenantId}/${pageId}/versions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        version_type: 'draft',
        layout_data: JSON.stringify(layoutData), // Store JSON for editing
        content: htmlContent, // Store HTML for display
        full_html: completeHTML, // Store complete HTML page
        restored_from: selectedVersionId,
        component_count: Array.isArray(layoutData) ? layoutData.length : 0,
        generated_at: new Date().toISOString()
      })
    });
    
    if (response.error) {
      throw new Error(response.error);
    }
    
    // Load the restored version into the current canvas
    const finalLayout = Array.isArray(layoutData) ? layoutData : [];
    console.log('Setting restored layout:', finalLayout);
    
    setLayout(finalLayout);
    setIsDirty(true); // Mark as dirty since this is a new working version
    
    // Refresh versions to show the new restored version
    await fetchPage();
    
    toast({
      title: 'Version restored',
      description: `Restored ${versionToRestore.version_type} version as new draft with ${finalLayout.length} components`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    
  } catch (error) {
    console.error('Error restoring version:', error);
    toast({
      title: 'Error restoring version',
      description: error.message || 'Failed to restore version',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  } finally {
    setIsRestoringVersion(false);
    onRestoreDialogClose();
    setSelectedVersionId(null);
  }
};

  const DynamicFormField = ({ field, value, onChange }) => {
    const { name, type, label, required, options, min, max, default: defaultValue, source } = field;
    const [selectOptions, setSelectOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const tenantId = user?.tenant_ids?.[0];

    // Fetch dynamic options when source is specified
    useEffect(() => {
      if (source && tenantId) {
        setLoading(true);
        fetchSelectOptions(source, tenantId)
          .then(setSelectOptions)
          .finally(() => setLoading(false));
      }
    }, [source, tenantId]);

    switch (type) {
      case 'text':
      case 'url':
        return (
          <FormControl isRequired={required}>
            <FormLabel>{label}</FormLabel>
            <Input
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={defaultValue || `Enter ${label.toLowerCase()}`}
              type={type === 'url' ? 'url' : 'text'}
            />
          </FormControl>
        );

      case 'textarea':
        return (
          <FormControl isRequired={required}>
            <FormLabel>{label}</FormLabel>
            <Textarea
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={defaultValue || `Enter ${label.toLowerCase()}`}
              rows={3}
            />
          </FormControl>
        );

      case 'richtext':
        return (
          <FormControl isRequired={required}>
            <FormLabel>{label}</FormLabel>
            <Textarea
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={defaultValue || `Enter ${label.toLowerCase()}`}
              rows={4}
            />
            <Text fontSize="xs" color="gray.500">
              Rich text editor (HTML supported)
            </Text>
          </FormControl>
        );

      case 'number':
        return (
          <FormControl isRequired={required}>
            <FormLabel>{label}</FormLabel>
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(parseInt(e.target.value) || 0)}
              placeholder={defaultValue || '0'}
              min={min}
              max={max}
            />
          </FormControl>
        );

      case 'select':
        // Use dynamic options if source is specified, otherwise use static options
        const optionsToUse = source ? selectOptions : (options || []);
        
        return (
          <FormControl isRequired={required}>
            <FormLabel>{label}</FormLabel>
            <Select
              value={value || defaultValue || ''}
              onChange={(e) => onChange(e.target.value)}
              isDisabled={loading}
            >
              <option value="">Select {label}</option>
              {optionsToUse.map((option) => {
                // Handle both string options and object options
                const optionValue = typeof option === 'object' ? option.value : option;
                const optionLabel = typeof option === 'object' ? option.label : 
                                    option.charAt(0).toUpperCase() + option.slice(1);
                
                return (
                  <option key={optionValue} value={optionValue}>
                    {optionLabel}
                  </option>
                );
              })}
            </Select>
            {loading && (
              <Text fontSize="xs" color="gray.500">Loading options...</Text>
            )}
          </FormControl>
        );

      // Add a new case for content model entries
      case 'content_model_entries':
        return (
          <ContentModelEntriesField
            field={field}
            value={value}
            onChange={onChange}
            tenantId={tenantId}
          />
        );

      case 'boolean':
        return (
          <FormControl display="flex" alignItems="center">
            <FormLabel mb="0">{label}</FormLabel>
            <Switch
              isChecked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
            />
          </FormControl>
        );

      case 'color':
        return (
          <FormControl isRequired={required}>
            <FormLabel>{label}</FormLabel>
            <Input
              type="color"
              value={value || defaultValue || '#000000'}
              onChange={(e) => onChange(e.target.value)}
            />
          </FormControl>
        );

      case 'media':
        return (
          <FormControl isRequired={required}>
            <FormLabel>{label}</FormLabel>
            <Input
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Enter media URL or upload path"
            />
            <Text fontSize="xs" color="gray.500">
              Media picker coming soon
            </Text>
          </FormControl>
        );

      case 'media_multiple':
        return (
          <FormControl isRequired={required}>
            <FormLabel>{label}</FormLabel>
            <Textarea
              value={Array.isArray(value) ? value.join('\n') : value || ''}
              onChange={(e) => onChange(e.target.value.split('\n').filter(url => url.trim()))}
              placeholder="Enter one media URL per line"
              rows={3}
            />
            <Text fontSize="xs" color="gray.500">
              Multiple media picker coming soon - one URL per line
            </Text>
          </FormControl>
        );

      default:
        return (
          <FormControl isRequired={required}>
            <FormLabel>{label}</FormLabel>
            <Input
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={defaultValue || `Enter ${label.toLowerCase()}`}
            />
            <Text fontSize="xs" color="gray.500">
              Unknown field type: {type}
            </Text>
          </FormControl>
        );
    }
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (!page) {
    return (
      <Container maxW="container.2xl" py={6}>
        <VStack spacing={4}>
          <Text>Page not found</Text>
          <Button onClick={() => navigate('/pages')}>
            Back to Pages
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box h="100vh" display="flex" flexDirection="column">
        {/* Header */}
        <Box px={6} py={4} borderBottom="1px" borderColor={borderColor}>
          <VStack spacing={3} align="stretch">
            <Breadcrumb separator={<ChevronRightIcon color="gray.500" />}>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/pages">Pages</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink>Page Builder</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>

            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Heading size="md">{page.name || 'Untitled Page'}</Heading>
                <HStack>
                  <Badge colorScheme="blue">{page.status || 'draft'}</Badge>
                  {isDirty && <Badge colorScheme="orange">Unsaved Changes</Badge>}
                  {workingVersion && !isDirty && (
                    <Badge colorScheme="purple">Working Version</Badge>
                  )}
                  {currentVersion && (
                    <Badge colorScheme="green">
                      {currentVersion.version_type === 'published' ? 'Published' : 'Draft'}
                    </Badge>
                  )}
                </HStack>
              </VStack>
              
              <HStack>
                <Button
                  leftIcon={<TimeIcon />}
                  variant="outline"
                  onClick={() => setIsVersionModalOpen(true)}
                >
                  Versions ({versions.length})
                </Button>
                <Button
                  leftIcon={<ViewIcon />}
                  variant="outline"
                  onClick={previewPage}
                >
                  Preview
                </Button>
                <Button
                  leftIcon={<DownloadIcon />}
                  onClick={saveLayout}
                  colorScheme="blue"
                  variant="outline"
                  isDisabled={!isDirty}
                >
                  Save Draft
                </Button>
                <Button
                  colorScheme="green"
                  onClick={publishPage}
                >
                  Publish
                </Button>
                <Button
                  leftIcon={<DownloadIcon />}
                  variant="outline"
                  onClick={exportHTML}
                  size="sm"
                >
                  Export HTML
                </Button>
              </HStack>
            </HStack>
          </VStack>
        </Box>

        {/* Main Content */}
        <Box flex={1} display="flex">
          {/* Component Sidebar */}
          <Box w="320px" bg={sidebarBg} borderRight="1px" borderColor={borderColor} overflowY="auto">
            <VStack spacing={4} align="stretch" p={4}>
              <Heading size="sm">Components</Heading>
              
              <Tabs size="sm">
                <TabList>
                  <Tab>Components</Tab>
                  <Tab>Settings</Tab>
                </TabList>
                
                <TabPanels>
                  <TabPanel p={0} pt={4}>
                    <ComponentsSidebar components={availableComponents} />
                  </TabPanel>
                  <TabPanel p={0} pt={4}>
                    <PageSettings page={page} onUpdate={fetchPage} />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          </Box>

          {/* Canvas Area */}
          <Box flex={1} display="flex" flexDirection="column">
            <Box flex={1} p={4} overflowY="auto">
              <PageCanvas 
                layout={layout}
                onEditComponent={(id) => {
                  const foundComponent = layout.find(item => item.id === id);
                  setSelectedComponent(id);
                  setIsPropsModalOpen(true);
                }}
                onRemoveComponent={removeComponent}
              />
            </Box>
          </Box>
        </Box>

        {/* Component Props Modal */}
        <ComponentPropsModal
          isOpen={isPropsModalOpen}
          onClose={() => setIsPropsModalOpen(false)}
          component={layout.find(item => item.id === selectedComponent)}
          onSave={(props) => {
            updateComponentProps(selectedComponent, props);
            setIsPropsModalOpen(false);
          }}
        />

        {/* Versions Modal */}
        <Modal isOpen={isVersionModalOpen} onClose={() => setIsVersionModalOpen(false)} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Page Versions</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text color="gray.600">
                  Manage and navigate between different versions of your page. 
                  You can load any version to continue editing or restore it as a new draft.
                </Text>
                
                {workingVersion && (
                  <Box p={4} bg="purple.50" borderRadius="md" border="1px" borderColor="purple.200">
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <HStack>
                          <Badge colorScheme="purple">Working Version</Badge>
                          <Text fontSize="sm" fontWeight="medium">
                            Current unsaved changes
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          Last modified: {new Date(workingVersion.timestamp).toLocaleString()}
                        </Text>
                      </VStack>
                      <Button
                        size="sm"
                        colorScheme="purple"
                        variant="outline"
                        onClick={() => {
                          setLayout(workingVersion.layout);
                          setIsDirty(workingVersion.isDirty);
                          setIsVersionModalOpen(false);
                        }}
                      >
                        Load
                      </Button>
                    </HStack>
                  </Box>
                )}
                
                <Divider />
                
                <Box maxH="400px" overflowY="auto">
                  <VStack spacing={3} align="stretch">
                    {versions.map((version) => (
                      <VersionCard
                        key={version.id}
                        version={version}
                        isCurrentVersion={currentVersion?.id === version.id}
                        onLoad={() => {
                          loadVersion(version);
                          setIsVersionModalOpen(false);
                        }}
                        onRestore={() => handleRestoreVersion(version.id)}
                      />
                    ))}
                    
                    {versions.length === 0 && (
                      <Text color="gray.500" textAlign="center" py={8}>
                        No versions found. Save your first draft to see versions here.
                      </Text>
                    )}
                  </VStack>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setIsVersionModalOpen(false)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Restore Version Confirmation Dialog */}
        <AlertDialog
          isOpen={isRestoreDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={onRestoreDialogClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Restore Version
              </AlertDialogHeader>
              <AlertDialogBody>
                Are you sure you want to restore this version? This will create a new draft 
                version based on the selected version. Your current unsaved changes will be lost.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onRestoreDialogClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={confirmRestoreVersion}
                  ml={3}
                  isLoading={isRestoringVersion}
                >
                  Restore
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Drag Overlay */}
        <DragOverlay
          dropAnimation={{
            sideEffects: defaultDropAnimationSideEffects({
              styles: {
                active: {
                  opacity: '0.5',
                },
              },
            }),
          }}
        >
          {activeId && draggedComponent ? (
            <Box
              bg="white"
              p={3}
              borderRadius="md"
              border="1px"
              borderColor="blue.300"
              shadow="lg"
              cursor="grabbing"
            >
              {activeId.toString().startsWith('component-') ? (
                <HStack>
                  <DragHandleIcon color="gray.400" />
                  <VStack align="start" spacing={1} flex={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      {draggedComponent.name || 'Unnamed Component'}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={2}>
                      {draggedComponent.description || draggedComponent.component_type || 'No description'}
                    </Text>
                  </VStack>
                </HStack>
              ) : (
                <ComponentPreview component={draggedComponent} />
              )}
            </Box>
          ) : null}
        </DragOverlay>
      </Box>
    </DndContext>
  );
};

export default PageBuilder;

// Version Card Component
const VersionCard = ({ version, isCurrentVersion, onLoad, onRestore }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getVersionIcon = (versionType) => {
    switch (versionType) {
      case 'published':
        return <CheckCircleIcon color="green.500" />;
      case 'draft':
        return <EditIcon color="blue.500" />;
      default:
        return <InfoIcon color="gray.500" />;
    }
  };

  const getVersionColor = (versionType) => {
    switch (versionType) {
      case 'published':
        return 'green';
      case 'draft':
        return 'blue';
      default:
        return 'gray';
    }
  };

  // Debug: Check if version has layout data
  const hasLayoutData = version.layout_data && (
    (typeof version.layout_data === 'string' && version.layout_data.length > 0) ||
    (Array.isArray(version.layout_data) && version.layout_data.length > 0) ||
    (typeof version.layout_data === 'object' && Object.keys(version.layout_data).length > 0)
  );

  const getComponentCount = () => {
    try {
      let layout = version.layout_data;
      if (typeof layout === 'string') {
        layout = JSON.parse(layout);
      }
      return Array.isArray(layout) ? layout.length : 0;
    } catch {
      return 0;
    }
  };

  return (
    <Box
      p={4}
      bg={isCurrentVersion ? 'blue.50' : 'white'}
      borderRadius="md"
      border="1px"
      borderColor={isCurrentVersion ? 'blue.200' : 'gray.200'}
    >
      <HStack justify="space-between" align="start">
        <HStack spacing={3} flex={1}>
          <Box>
            {getVersionIcon(version.version_type)}
          </Box>
          <VStack align="start" spacing={1} flex={1}>
            <HStack>
              <Badge colorScheme={getVersionColor(version.version_type)}>
                {version.version_type}
              </Badge>
              {isCurrentVersion && (
                <Badge colorScheme="blue" variant="outline">
                  Current
                </Badge>
              )}
              {version.restored_from && (
                <Badge colorScheme="purple" variant="outline">
                  Restored
                </Badge>
              )}
            </HStack>
            <Text fontSize="sm" fontWeight="medium">
              Created: {formatDate(version.created_at)}
            </Text>
            <Text fontSize="xs" color="gray.500">
              Components: {getComponentCount()} • Has Data: {hasLayoutData ? 'Yes' : 'No'}
            </Text>
            {version.comment && (
              <Text fontSize="xs" color="gray.600">
                {version.comment}
              </Text>
            )}
          </VStack>
        </HStack>
        
        <HStack>
          <Tooltip label="Load this version for editing">
            <Button
              size="sm"
              variant="outline"
              onClick={onLoad}
              isDisabled={isCurrentVersion}
            >
              Load
            </Button>
          </Tooltip>
          <Tooltip label="Restore this version as a new draft">
            <IconButton
              size="sm"
              icon={<RepeatIcon />}
              variant="outline"
              onClick={onRestore}
              aria-label="Restore version"
            />
          </Tooltip>
        </HStack>
      </HStack>
    </Box>
  );
};

// Draggable Component Card
const DraggableComponentCard = ({ component }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `component-${component.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      w="full"
      p={3}
      bg="white"
      borderRadius="md"
      border="1px"
      borderColor="gray.200"
      cursor="grab"
      _hover={{ borderColor: 'blue.300', shadow: 'sm' }}
    >
      <HStack>
        <DragHandleIcon color="gray.400" />
        <VStack align="start" spacing={1} flex={1}>
          <Text fontSize="sm" fontWeight="medium">
            {component.name || 'Unnamed Component'}
          </Text>
          <Text fontSize="xs" color="gray.500" noOfLines={2}>
            {component.description || component.component_type || 'No description'}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

// Components Sidebar Component
const ComponentsSidebar = ({ components = [] }) => {
  const groupedComponents = components.reduce((acc, component) => {
    const category = component.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(component);
    return acc;
  }, {});

  return (
    <VStack spacing={3} align="stretch">
      {Object.keys(groupedComponents).length === 0 ? (
        <Text color="gray.500" textAlign="center" py={4}>
          No components available
        </Text>
      ) : (
        <Accordion allowMultiple defaultIndex={[0]}>
          {Object.entries(groupedComponents).map(([category, categoryComponents]) => (
            <AccordionItem key={category}>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Text fontWeight="medium">{category}</Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <VStack spacing={2}>
                  {categoryComponents.map((component) => (
                    <DraggableComponentCard
                      key={component.id}
                      component={component}
                    />
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </VStack>
  );
};

// Sortable Layout Item
const SortableLayoutItem = ({ item, onEdit, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit(item.id);
  };

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove(item.id);
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      mb={4}
      position="relative"
      border="1px"
      borderColor="gray.200"
      borderRadius="md"
      _hover={{ borderColor: 'blue.300' }}
      bg="white"
    >
      <HStack
        position="absolute"
        top={2}
        right={2}
        spacing={1}
        bg="white"
        p={1}
        borderRadius="md"
        shadow="sm"
        zIndex={10}
      >
        <IconButton
          {...attributes}
          {...listeners}
          size="xs"
          icon={<DragHandleIcon />}
          variant="ghost"
          cursor="grab"
          aria-label="Drag component"
        />
        <IconButton
          size="xs"
          icon={<EditIcon />}
          variant="ghost"
          onClick={handleEdit}
          aria-label="Edit component"
        />
        <IconButton
          size="xs"
          icon={<DeleteIcon />}
          variant="ghost"
          colorScheme="red"
          onClick={handleRemove}
          aria-label="Remove component"
        />
      </HStack>

      <Box p={4}>
        <ComponentPreview component={item} />
      </Box>
    </Box>
  );
};

// Droppable Page Canvas
const DroppableCanvas = ({ children }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'layout-droppable',
  });

  return (
    <Box
      ref={setNodeRef}
      minH="600px"
      bg={isOver ? "blue.50" : "white"}
      border="2px dashed"
      borderColor={isOver ? "blue.400" : "gray.200"}
      borderRadius="md"
      p={6}
      position="relative"
      transition="all 0.2s ease"
      _hover={{
        borderColor: "blue.300",
        bg: "gray.50"
      }}
    >
      {children}
    </Box>
  );
};

// Page Canvas Component
const PageCanvas = ({ layout = [], onEditComponent, onRemoveComponent }) => {
  const layoutIds = layout.map(item => item.id);

  return (
    <SortableContext items={layoutIds} strategy={verticalListSortingStrategy}>
      <DroppableCanvas>
        {layout.map((item) => (
          <SortableLayoutItem
            key={item.id}
            item={item}
            onEdit={onEditComponent}
            onRemove={onRemoveComponent}
          />
        ))}
        
        {layout.length === 0 && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            textAlign="center"
            color="gray.500"
          >
            <VStack spacing={2}>
              <AddIcon boxSize={8} />
              <Text fontSize="lg">Drag components here to build your page</Text>
              <Text fontSize="sm">Start by dragging components from the sidebar</Text>
            </VStack>
          </Box>
        )}
      </DroppableCanvas>
    </SortableContext>
  );
};

// Component Preview (same as before - keeping the existing comprehensive preview logic)
const ComponentPreview = ({ component }) => {
  const { type, props = {}, name, identifier } = component;

  switch (identifier || type) {
    case 'navigation-menu':
    case 'navigation':
      return (
        <Box p={3} bg="gray.50" borderRadius="md">
          <Text fontWeight="bold" mb={2}>Navigation Menu</Text>
          {props.navigation_id ? (
            <Text fontSize="sm" color="blue.600" mb={2}>
              Navigation ID: {props.navigation_id}
            </Text>
          ) : (
            <Text fontSize="sm" color="orange.500" mb={2}>
              No navigation selected
            </Text>
          )}
          <HStack spacing={4}>
            <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.500' }}>Home</Text>
            <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.500' }}>About</Text>
            <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.500' }}>Services</Text>
            <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.500' }}>Contact</Text>
          </HStack>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Style: {props.style || 'horizontal'} • Icons: {props.show_icons ? 'Yes' : 'No'}
          </Text>
        </Box>
      );

    case 'content-list':
    case 'list':
      return (
        <Box p={4} bg="gray.50" borderRadius="md">
          <Text fontWeight="bold" mb={3}>Content List</Text>
          {props.content_model_id ? (
            <Text fontSize="sm" color="blue.600" mb={2}>
              Content Model ID: {props.content_model_id}
            </Text>
          ) : props.content_entry_ids ? (
            <Text fontSize="sm" color="blue.600" mb={2}>
              Specific Entries: {Array.isArray(props.content_entry_ids) ? props.content_entry_ids.length : 1}
            </Text>
          ) : (
            <Text fontSize="sm" color="orange.500" mb={2}>
              No content source selected
            </Text>
          )}
          <VStack spacing={2} align="stretch">
            {[1, 2, 3].map(i => (
              <Box key={i} p={2} bg="white" borderRadius="sm" border="1px" borderColor="gray.200">
                <Text fontSize="sm" fontWeight="medium">Content Item {i}</Text>
                {props.show_excerpt && (
                  <Text fontSize="xs" color="gray.600">Sample excerpt text...</Text>
                )}
                {props.show_date && (
                  <Text fontSize="xs" color="gray.500">July {i}, 2025</Text>
                )}
              </Box>
            ))}
          </VStack>
          <Text fontSize="xs" color="gray.500" mt={2}>
            Limit: {props.limit || 10} • Layout: {props.layout || 'list'}
          </Text>
        </Box>
      );

    case 'hero-section':
    case 'hero':
      return (
        <Box
          bg={props.background_color || props.backgroundColor || 'gray.100'}
          p={8}
          borderRadius="md"
          textAlign="center"
          color={props.text_color || props.textColor || 'inherit'}
        >
          <VStack spacing={4}>
            <Heading size="xl">
              {props.title || 'Hero Section'}
            </Heading>
            <Text fontSize="lg" color="gray.600">
              {props.subtitle || 'Hero subtitle goes here'}
            </Text>
            {props.cta_text && (
              <Button colorScheme="blue" size="lg">
                {props.cta_text}
              </Button>
            )}
          </VStack>
        </Box>
      );

    case 'text-block':
    case 'text':
    case 'paragraph':
      return (
        <Box
          textAlign={props.alignment || 'left'}
          maxW={props.max_width !== 'none' ? props.max_width : undefined}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: props.content || props.text || 'Text content goes here...'
            }}
            style={{
              fontSize: props.font_size === 'sm' ? '14px' :
                       props.font_size === 'lg' ? '18px' :
                       props.font_size === 'xl' ? '20px' : '16px'
            }}
          />
        </Box>
      );

    case 'image-gallery':
    case 'gallery':
      return (
        <Box>
          <Text fontWeight="bold" mb={2}>Image Gallery</Text>
          <Box
            display="grid"
            gridTemplateColumns={`repeat(${props.columns || 3}, 1fr)`}
            gap={2}
            p={4}
            bg="gray.50"
            borderRadius="md"
          >
            {[1, 2, 3].map(i => (
              <Box
                key={i}
                h="100px"
                bg="gray.200"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="xs" color="gray.500">Image {i}</Text>
              </Box>
            ))}
          </Box>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Layout: {props.layout || 'grid'} • Columns: {props.columns || 3}
          </Text>
        </Box>
      );

    case 'content-list':
    case 'list':
      return (
        <Box p={4} bg="gray.50" borderRadius="md">
          <Text fontWeight="bold" mb={3}>Content List</Text>
          <VStack spacing={2} align="stretch">
            {[1, 2, 3].map(i => (
              <Box key={i} p={2} bg="white" borderRadius="sm" border="1px" borderColor="gray.200">
                <Text fontSize="sm" fontWeight="medium">Content Item {i}</Text>
                {props.show_excerpt && (
                  <Text fontSize="xs" color="gray.600">Sample excerpt text...</Text>
                )}
                {props.show_date && (
                  <Text fontSize="xs" color="gray.500">July {i}, 2025</Text>
                )}
              </Box>
            ))}
          </VStack>
          <Text fontSize="xs" color="gray.500" mt={2}>
            Limit: {props.limit || 10} • Layout: {props.layout || 'grid'}
          </Text>
        </Box>
      );

    case 'navigation-menu':
    case 'navigation':
      return (
        <Box p={3} bg="gray.50" borderRadius="md">
          <Text fontWeight="bold" mb={2}>Navigation Menu</Text>
          {props.navigation_id ? (
            <Text fontSize="sm" color="blue.600" mb={2}>
              Navigation ID: {props.navigation_id}
            </Text>
          ) : (
            <Text fontSize="sm" color="orange.500" mb={2}>
              No navigation selected
            </Text>
          )}
          <HStack spacing={4}>
            <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.500' }}>Home</Text>
            <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.500' }}>About</Text>
            <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.500' }}>Services</Text>
            <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.500' }}>Contact</Text>
          </HStack>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Style: {props.style || 'horizontal'} • Icons: {props.show_icons ? 'Yes' : 'No'}
          </Text>
        </Box>
      );

    case 'heading':
    case 'title':
      return (
        <Heading size={props.size || 'lg'} color={props.color}>
          {props.text || props.title || 'Heading Component'}
        </Heading>
      );

    case 'image':
      return (
        <Box
          h={props.height || '200px'}
          bg="gray.100"
          borderRadius="md"
          display="flex"
          alignItems="center"
          justifyContent="center"
          backgroundImage={props.src ? `url(${props.src})` : undefined}
          backgroundSize="cover"
          backgroundPosition="center"
        >
          {!props.src && (
            <Text color="gray.500">Image Component</Text>
          )}
        </Box>
      );

    case 'button':
      return (
        <Button
          colorScheme={props.colorScheme || 'blue'}
          size={props.size || 'md'}
          variant={props.variant || 'solid'}
        >
          {props.text || props.label || 'Button'}
        </Button>
      );

    case 'card':
      return (
        <Box
          bg="white"
          p={6}
          borderRadius="md"
          border="1px"
          borderColor="gray.200"
          shadow="sm"
        >
          <Heading size="md" mb={2}>
            {props.title || 'Card Title'}
          </Heading>
          <Text color="gray.600">
            {props.content || 'Card content goes here...'}
          </Text>
        </Box>
      );

    default:
      return (
        <Box
          p={4}
          bg="gray.50"
          borderRadius="md"
          textAlign="center"
          border="1px"
          borderColor="gray.200"
        >
          <Text color="gray.600" fontWeight="medium">
            {name || identifier || type || 'Unknown'} Component
          </Text>
          {identifier && identifier !== type && (
            <Text color="gray.400" fontSize="sm">
              Type: {type || identifier}
            </Text>
          )}
          {Object.keys(props).length > 0 && (
            <Text color="gray.400" fontSize="xs" mt={1}>
              {Object.keys(props).length} properties configured
            </Text>
          )}
        </Box>
      );
  }
};

// Page Settings Component
const PageSettings = ({ page, onUpdate }) => {
  const [settings, setSettings] = useState({
    meta_title: page?.meta_title || '',
    meta_description: page?.meta_description || '',
    is_home: page?.is_home || false
  });

  const handleSave = async () => {
    try {
      console.log('Saving page settings:', settings);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving page settings:', error);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <Heading size="sm">Page Settings</Heading>
      
      <FormControl>
        <FormLabel fontSize="sm">Meta Title</FormLabel>
        <Input
          size="sm"
          value={settings.meta_title}
          onChange={(e) => setSettings({ ...settings, meta_title: e.target.value })}
          placeholder="Page meta title"
        />
      </FormControl>
      
      <FormControl>
        <FormLabel fontSize="sm">Meta Description</FormLabel>
        <Textarea
          size="sm"
          value={settings.meta_description}
          onChange={(e) => setSettings({ ...settings, meta_description: e.target.value })}
          placeholder="Page meta description"
          rows={3}
        />
      </FormControl>
      
      <Button size="sm" colorScheme="blue" onClick={handleSave}>
        Save Settings
      </Button>
    </VStack>
  );
};

// Component Props Modal
const ComponentPropsModal = ({ isOpen, onClose, component, onSave }) => {
  const [props, setProps] = useState({});

  useEffect(() => {
    if (component) {
      setProps(component.props || {});
    }
  }, [component]);

  const handleSave = () => {
    onSave(props);
  };

  if (!component) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit {component.name || component.type} Component</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <ComponentPropsForm
            componentType={component.type}
            props={props}
            onChange={setProps}
            component={component}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Component Props Form
const ComponentPropsForm = ({ componentType, props, onChange, component }) => {
  const updateProp = (key, value) => {
    onChange({ ...props, [key]: value });
  };

  if (component && component.fields && Array.isArray(component.fields)) {
    return (
      <VStack spacing={4}>
        {component.fields.map((field, index) => (
          <DynamicFormField
            key={field.name || index}
            field={field}
            value={props[field.name] || field.default || ''}
            onChange={(value) => updateProp(field.name, value)}
          />
        ))}
        
        <FormControl>
          <FormLabel fontSize="sm" color="gray.500">Advanced (JSON)</FormLabel>
          <Textarea
            value={JSON.stringify(props, null, 2)}
            onChange={(e) => {
              try {
                const newProps = JSON.parse(e.target.value);
                onChange(newProps);
              } catch (err) {
                // Invalid JSON, ignore
              }
            }}
            placeholder='{"key": "value"}'
            rows={3}
            fontFamily="mono"
            fontSize="xs"
            size="sm"
          />
        </FormControl>
      </VStack>
    );
  }

  switch (componentType) {
    case 'heading':
    case 'title':
      return (
        <VStack spacing={4}>
          <FormControl>
            <FormLabel>Text</FormLabel>
            <Input
              value={props.text || props.title || ''}
              onChange={(e) => updateProp(props.title !== undefined ? 'title' : 'text', e.target.value)}
              placeholder="Heading text"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Size</FormLabel>
            <Select
              value={props.size || 'lg'}
              onChange={(e) => updateProp('size', e.target.value)}
            >
              <option value="sm">Small</option>
              <option value="md">Medium</option>
              <option value="lg">Large</option>
              <option value="xl">Extra Large</option>
              <option value="2xl">2X Large</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Color</FormLabel>
            <Input
              type="color"
              value={props.color || '#000000'}
              onChange={(e) => updateProp('color', e.target.value)}
            />
          </FormControl>
        </VStack>
      );
      
    case 'text':
    case 'paragraph':
      return (
        <VStack spacing={4}>
          <FormControl>
            <FormLabel>Content</FormLabel>
            <Textarea
              value={props.content || props.text || ''}
              onChange={(e) => updateProp(props.content !== undefined ? 'content' : 'text', e.target.value)}
              placeholder="Text content"
              rows={4}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Font Size</FormLabel>
            <Select
              value={props.size || 'md'}
              onChange={(e) => updateProp('size', e.target.value)}
            >
              <option value="xs">Extra Small</option>
              <option value="sm">Small</option>
              <option value="md">Medium</option>
              <option value="lg">Large</option>
              <option value="xl">Extra Large</option>
            </Select>
          </FormControl>
        </VStack>
      );
      
    default:
      return (
        <VStack spacing={4}>
          <Text>Component type "{componentType}" detected</Text>
          
          <FormControl>
            <FormLabel>Text Content</FormLabel>
            <Input
              value={props.text || props.content || props.title || ''}
              onChange={(e) => {
                const key = props.text !== undefined ? 'text' : 
                           props.content !== undefined ? 'content' : 'title';
                updateProp(key, e.target.value);
              }}
              placeholder="Enter text content"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Advanced (JSON)</FormLabel>
            <Textarea
              value={JSON.stringify(props, null, 2)}
              onChange={(e) => {
                try {
                  const newProps = JSON.parse(e.target.value);
                  onChange(newProps);
                } catch (err) {
                  // Invalid JSON, ignore
                }
              }}
              placeholder='{"key": "value"}'
              rows={4}
              fontFamily="mono"
              fontSize="sm"
            />
          </FormControl>
        </VStack>
      );
  }
};

// Dynamic Form Field Component
const DynamicFormField = ({ field, value, onChange }) => {
  const { name, type, label, required, options, min, max, default: defaultValue, source } = field;
  const [selectOptions, setSelectOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  // Fetch dynamic options when source is specified
  useEffect(() => {
    if (source && tenantId) {
      setLoading(true);
      fetchSelectOptions(source, tenantId)
        .then(setSelectOptions)
        .finally(() => setLoading(false));
    }
  }, [source, tenantId]);

  switch (type) {
    case 'text':
    case 'url':
      return (
        <FormControl isRequired={required}>
          <FormLabel>{label}</FormLabel>
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={defaultValue || `Enter ${label.toLowerCase()}`}
            type={type === 'url' ? 'url' : 'text'}
          />
        </FormControl>
      );

    case 'textarea':
      return (
        <FormControl isRequired={required}>
          <FormLabel>{label}</FormLabel>
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={defaultValue || `Enter ${label.toLowerCase()}`}
            rows={3}
          />
        </FormControl>
      );

    case 'richtext':
      return (
        <FormControl isRequired={required}>
          <FormLabel>{label}</FormLabel>
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={defaultValue || `Enter ${label.toLowerCase()}`}
            rows={4}
          />
          <Text fontSize="xs" color="gray.500">
            Rich text editor (HTML supported)
          </Text>
        </FormControl>
      );

    case 'number':
      return (
        <FormControl isRequired={required}>
          <FormLabel>{label}</FormLabel>
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(parseInt(e.target.value) || 0)}
            placeholder={defaultValue || '0'}
            min={min}
            max={max}
          />
        </FormControl>
      );

    case 'select':
      // Use dynamic options if source is specified, otherwise use static options
      const optionsToUse = source ? selectOptions : (options || []);
      
      return (
        <FormControl isRequired={required}>
          <FormLabel>{label}</FormLabel>
          <Select
            value={value || defaultValue || ''}
            onChange={(e) => onChange(e.target.value)}
            isDisabled={loading}
          >
            <option value="">Select {label}</option>
            {optionsToUse.map((option) => {
              // Handle both string options and object options
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : 
                                  option.charAt(0).toUpperCase() + option.slice(1);
              
              return (
                <option key={optionValue} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </Select>
          {loading && (
            <Text fontSize="xs" color="gray.500">Loading options...</Text>
          )}
        </FormControl>
      );

    // Add a new case for content model entries
    case 'content_model_entries':
      return (
        <ContentModelEntriesField
          field={field}
          value={value}
          onChange={onChange}
          tenantId={tenantId}
        />
      );

    case 'boolean':
      return (
        <FormControl display="flex" alignItems="center">
          <FormLabel mb="0">{label}</FormLabel>
          <Switch
            isChecked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
          />
        </FormControl>
      );

    case 'color':
      return (
        <FormControl isRequired={required}>
          <FormLabel>{label}</FormLabel>
          <Input
            type="color"
            value={value || defaultValue || '#000000'}
            onChange={(e) => onChange(e.target.value)}
          />
        </FormControl>
      );

    case 'media':
      return (
        <FormControl isRequired={required}>
          <FormLabel>{label}</FormLabel>
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter media URL or upload path"
          />
          <Text fontSize="xs" color="gray.500">
            Media picker coming soon
          </Text>
        </FormControl>
      );

    case 'media_multiple':
      return (
        <FormControl isRequired={required}>
          <FormLabel>{label}</FormLabel>
          <Textarea
            value={Array.isArray(value) ? value.join('\n') : value || ''}
            onChange={(e) => onChange(e.target.value.split('\n').filter(url => url.trim()))}
            placeholder="Enter one media URL per line"
            rows={3}
          />
          <Text fontSize="xs" color="gray.500">
            Multiple media picker coming soon - one URL per line
          </Text>
        </FormControl>
      );

    default:
      return (
        <FormControl isRequired={required}>
          <FormLabel>{label}</FormLabel>
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={defaultValue || `Enter ${label.toLowerCase()}`}
          />
          <Text fontSize="xs" color="gray.500">
            Unknown field type: {type}
          </Text>
        </FormControl>
      );
  }
};

// Add this component inside PageBuilder.js or as a separate component

const ContentModelEntriesField = ({ field, value, onChange, tenantId }) => {
  const [contentModels, setContentModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tenantId) {
      fetchContentModels();
    }
  }, [tenantId]);

  useEffect(() => {
    if (selectedModel && tenantId) {
      fetchEntriesForModel(selectedModel);
    }
  }, [selectedModel, tenantId]);

  const fetchContentModels = async () => {
    try {
      const response = await apiClient(`/content-models/${tenantId}`);
      const models = response.data || response || [];
      setContentModels(models.map(model => ({
        value: model.id,
        label: model.name
      })));
    } catch (error) {
      console.error('Error fetching content models:', error);
    }
  };

  const fetchEntriesForModel = async (modelId) => {
    setLoading(true);
    try {
      const response = await apiClient(`/content-entries/${tenantId}?content_model_id=${modelId}`);
      const entriesData = response.data || response || [];
      setEntries(entriesData.map(entry => ({
        value: entry.id,
        label: entry.title || `Entry #${entry.id}`
      })));
    } catch (error) {
      console.error('Error fetching entries:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormControl isRequired={field.required}>
      <FormLabel>{field.label}</FormLabel>
      <VStack spacing={3} align="stretch">
        <Select
          placeholder="Select Content Model"
          value={selectedModel}
          onChange={(e) => {
            setSelectedModel(e.target.value);
            setEntries([]);
            onChange(''); // Reset entry selection
          }}
        >
          {contentModels.map(model => (
            <option key={model.value} value={model.value}>
              {model.label}
            </option>
          ))}
        </Select>

        {selectedModel && (
          <Select
            placeholder="Select Entry"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            isDisabled={loading}
          >
            {entries.map(entry => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </Select>
        )}

        {loading && (
          <Text fontSize="xs" color="gray.500">Loading entries...</Text>
        )}
      </VStack>
    </FormControl>
  );
};